import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import { extractFieldErrors } from '../../../../services/practiceDetail';
import { useCreateOrthodontist } from '../../../../hooks/useOrthodontistAccounts';
import {
    buildOrthodontistCreateBody,
    emptyOrthodontistFieldValues,
    ORTHODONTIST_REQUIRED_FIELDS,
    requiredOrthodontistFieldError
} from './orthodontistCreateFields';

// Create-only form state for OrthodontistCreatePage - one all-or-nothing POST,
// unlike the practice Details tab's incremental per-field PATCH saves.
export function useOrthodontistCreateForm() {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { mutateAsync, isPending } = useCreateOrthodontist();

    const [values, setValues] = useState<Record<string, string>>(() =>
        emptyOrthodontistFieldValues()
    );
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const onFieldChange = useCallback((key: string, value: string) => {
        setValues((prev) => ({ ...prev, [key]: value }));
        setFieldErrors((prev) => {
            if (!(key in prev)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    const canCreate = useMemo(
        () =>
            ORTHODONTIST_REQUIRED_FIELDS.every((field) => values[field.key]?.trim() !== '') &&
            !isPending,
        [values, isPending]
    );

    const onCreate = useCallback(async () => {
        if (!canCreate) return;

        const localErrors: Record<string, string> = {};
        for (const field of ORTHODONTIST_REQUIRED_FIELDS) {
            const message = requiredOrthodontistFieldError(field, values[field.key] ?? '');
            if (message) localErrors[field.key] = message;
        }
        if (Object.keys(localErrors).length > 0) {
            setFieldErrors(localErrors);
            return;
        }

        const body = buildOrthodontistCreateBody(values);
        const orthodontistName = values.name.trim();

        try {
            const created = await mutateAsync(body);
            showToast(`Orthodontist created - ${orthodontistName}`, 'success');
            navigate(`/accounts/orthodontists/${created.id}`);
        } catch (err) {
            const errors = extractFieldErrors(err);
            setFieldErrors(errors ?? {});
            showToast(
                apiErrorMessage(err, 'Failed to create orthodontist - nothing was saved'),
                'error'
            );
        }
    }, [canCreate, values, mutateAsync, showToast, navigate]);

    return {
        values,
        fieldError: (key: string) => fieldErrors[key],
        onFieldChange,
        isSaving: isPending,
        canCreate,
        onCreate: () => void onCreate()
    };
}
